const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

export async function fetchAuctionItems(eventId, customerEmail) {
  const url = customerEmail
    ? `${apiUrl}/events/${eventId}/auction/items/public?customer_email=${encodeURIComponent(customerEmail)}`
    : `${apiUrl}/events/${eventId}/auction/items/public`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch auction items: ${res.status}`);
  return res.json();
}

export async function placeBid(eventId, itemId, payload) {
  const res = await fetch(
    `${apiUrl}/events/${eventId}/auction/items/${itemId}/bids`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to place bid");
  return data;
}

export async function getPaymentStatus(eventId, customerToken) {
  const res = await fetch(
    `${apiUrl}/events/${eventId}/auction/payment-status?customer_token=${encodeURIComponent(customerToken)}`
  );
  if (!res.ok) throw new Error("Failed to fetch payment status");
  return res.json();
}

// ── Stripe card setup ──────────────────────────────────────────────────────────

export async function setupPayment(eventId, customerToken, successUrl, cancelUrl) {
  const res = await fetch(`${apiUrl}/events/${eventId}/auction/setup-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, success_url: successUrl, cancel_url: cancelUrl }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to start card setup");
  return data;
}

export async function confirmSetup(eventId, customerToken, sessionId, saveForNextTime = true) {
  const res = await fetch(`${apiUrl}/events/${eventId}/auction/confirm-setup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, session_id: sessionId, save_for_next_time: saveForNextTime }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to confirm card setup");
  return data;
}

// ── PayPal vault setup ─────────────────────────────────────────────────────────

export async function setupPaypal(eventId, customerToken, successUrl, cancelUrl) {
  const res = await fetch(`${apiUrl}/events/${eventId}/auction/setup-paypal`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, success_url: successUrl, cancel_url: cancelUrl }),
  });
  const data = await res.json();
  if (!res.ok) {
    console.error("[setupPaypal] HTTP", res.status, JSON.stringify(data));
    throw new Error(data.detail || "Failed to start PayPal setup");
  }
  return data;
}

export async function confirmPaypal(eventId, customerToken, approvalTokenId, saveForNextTime = true) {
  const res = await fetch(`${apiUrl}/events/${eventId}/auction/confirm-paypal`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, approval_token_id: approvalTokenId, save_for_next_time: saveForNextTime }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to confirm PayPal setup");
  return data;
}

// ── Global customer payment method management ──────────────────────────────────

export async function getCustomerPaymentMethod(customerToken) {
  const res = await fetch(
    `${apiUrl}/customers/payment-method?customer_token=${encodeURIComponent(customerToken)}`
  );
  if (!res.ok) throw new Error("Failed to fetch payment method");
  return res.json();
}

export async function removeCustomerPaymentMethod(customerToken) {
  const res = await fetch(
    `${apiUrl}/customers/payment-method?customer_token=${encodeURIComponent(customerToken)}`,
    { method: "DELETE" }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to remove payment method");
  return data;
}

export async function setupCustomerCard(customerToken, successUrl, cancelUrl) {
  const res = await fetch(`${apiUrl}/customers/payment-method/setup-card`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, success_url: successUrl, cancel_url: cancelUrl }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to start card setup");
  return data;
}

export async function confirmCustomerCard(customerToken, sessionId) {
  const res = await fetch(`${apiUrl}/customers/payment-method/confirm-card`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, session_id: sessionId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to confirm card setup");
  return data;
}

export async function setupCustomerPaypal(customerToken, successUrl, cancelUrl) {
  const res = await fetch(`${apiUrl}/customers/payment-method/setup-paypal`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, success_url: successUrl, cancel_url: cancelUrl }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to start PayPal setup");
  return data;
}

export async function confirmCustomerPaypal(customerToken, approvalTokenId) {
  const res = await fetch(`${apiUrl}/customers/payment-method/confirm-paypal`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, approval_token_id: approvalTokenId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to confirm PayPal setup");
  return data;
}

export async function setupCustomerVenmo(customerToken) {
  const res = await fetch(`${apiUrl}/customers/payment-method/setup-venmo`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to start Venmo setup");
  return data; // { token_id }
}

export async function confirmCustomerVenmo(customerToken, approvalTokenId) {
  const res = await fetch(`${apiUrl}/customers/payment-method/confirm-venmo`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, approval_token_id: approvalTokenId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to confirm Venmo setup");
  return data;
}

// ── Venmo vault setup (auction) ───────────────────────────────────────────────

export async function setupVenmoVault(eventId, customerToken) {
  const res = await fetch(`${apiUrl}/events/${eventId}/auction/setup-venmo`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to start Venmo setup");
  return data; // { token_id }
}

export async function confirmVenmo(eventId, customerToken, approvalTokenId, saveForNextTime = true) {
  const res = await fetch(`${apiUrl}/events/${eventId}/auction/confirm-venmo`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken, approval_token_id: approvalTokenId, save_for_next_time: saveForNextTime }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to confirm Venmo setup");
  return data;
}

// ── Winner fulfillment ─────────────────────────────────────────────────────────

export async function submitWinnerFulfillment(eventId, itemId, payload) {
  const res = await fetch(
    `${apiUrl}/events/${eventId}/auction/items/${itemId}/winner-fulfillment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to submit fulfillment");
  return data;
}

export async function retryWinnerPayment(eventId, itemId, customerToken) {
  const res = await fetch(
    `${apiUrl}/events/${eventId}/auction/items/${itemId}/retry-payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customer_token: customerToken }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Payment retry failed");
  return data;
}

export async function getAuctionWins(customerToken) {
  const res = await fetch(
    `${apiUrl}/customers/auction-wins?customer_token=${encodeURIComponent(customerToken)}`
  );
  if (!res.ok) throw new Error("Failed to fetch auction wins");
  return res.json();
}

// ── Buy Now ───────────────────────────────────────────────────────────────────

export async function buyNow(eventId, itemId, customerToken) {
  const res = await fetch(
    `${apiUrl}/events/${eventId}/auction/items/${itemId}/buy-now`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customer_token: customerToken }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Buy Now failed");
  return data;
}
